import { createSlice } from '@reduxjs/toolkit';

import { communicationApi } from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

export const forwardSlice = createSlice({
    name: 'forward',
    initialState: {
        pending: false,
        savePending: false,
        error: null,
        forwards: [],
        count: 0,
        total: 0,
        currentPage: 1,
        totalPages: 1,
        deletePending: false,
    },
    reducers: {
        start: (state, action) => {
            state.error = null;
            state.pending = true;
        },
        stop: (state, action) => {
            state.pending = false;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.pending = false;
        },
        setPending: (state, action) => {
            state.pending = action.payload;
        },
        setForwards: (state, action) => {
            state.pending = false;
            state.count = action.payload.count;
            state.total = action.payload.total;
            state.forwards = action.payload.items;
            state.currentPage = action.payload.currentPage;
            state.totalPages = action.payload.totalPages;
        },
        setSavePending: (state, action) => {
            state.savePending = action.payload;
        },
        addForward: (state, action) => {
            state.pending = false;
            state.forwards = [action.payload, ...state.forwards];
            state.total = state.total + 1;
            if (state.totalPages === 0) {
                state.totalPages = 1;
            }
        },
        editForward: (state, action) => {
            state.pending = false;
            const index = state.forwards.findIndex((forwards) => forwards.id === action.payload.id);
            if (index !== -1) {
                state.forwards = [
                    ...state.forwards.slice(0, index),
                    action.payload,
                    ...state.forwards.slice(index + 1),
                ];
            }
        },
        deleteForward: (state, action) => {
            state.deletePending = false;
            const index = state.forwards.findIndex((forward) => forward.id === action.payload.id);
            if (index !== -1) {
                state.forwards = [...state.forwards.slice(0, index), ...state.forwards.slice(index + 1)];
            }
            state.total = state.total - 1;
            if (state.total === 0) {
                state.totalPages = 0;
            }
        },
        startDelete: (state) => {
            state.deletePending = true;
        },
    },
});

export const {
    start,
    stop,
    setError,
    setForwards,
    setPending,
    setSavePending,
    addForward,
    editForward,
    deleteForward,
    startDelete,
} = forwardSlice.actions;

export const getForwardPending = (state) => state.forward.pending;
export const getForwardError = (state) => state.forward.error;
export const getPreviewForwardDialog = (state) => state.forward.previewForwardDialog;
export const getForwards = (state) => state.forward.forwards;
export const getSavePending = (state) => state.forward.savePending;
export const getForwardsTotal = (state) => state.forward.total;
export const getForwardsCurrentPage = (state) => state.forward.currentPage;
export const getForwardsTotalPages = (state) => state.forward.totalPages;

export const fetchForwardsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                let query = transformFiltersToQuery(queryParams);
                dispatch(start());
                communicationApi
                    .get('forwards' + query)
                    .then((response) => {
                        dispatch(setForwards(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setError(error));
                        if (onFailure) {
                            onFailure(error);
                        }
                    });
            } catch (e) {
                dispatch(stop());
                return console.error('error', e);
            }
        };


export const addForwardAction =
    ({ forwardDto, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(start());
                communicationApi
                    .post('forwards', forwardDto)
                    .then((response) => {
                        dispatch(addForward(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(stop());
                        onFailure?.(error.response.data);
                    });
            } catch (e) {
                dispatch(stop());
                return console.error(e.message);
            }
        };

export const updateForwardAction =
    ({ id, forwardDto, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(start());
                communicationApi
                    .put('forwards/' + id, forwardDto)
                    .then((response) => {
                        dispatch(editForward(response.data));
                        onSuccess?.(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(stop());
                        onFailure?.();
                    });
            } catch (e) {
                dispatch(stop());
                return console.error('error', e);
            }
        };

export const deleteForwardAction =
    ({ id, onSuccess, onFailure }) =>
        async (dispatch) => {
            try {
                dispatch(startDelete(id));
                communicationApi
                    .delete('forwards/' + id)
                    .then((response) => {
                        dispatch(deleteForward({ id }));
                        onSuccess?.();
                        return response;
                    })
                    .catch((error) => {
                        onFailure?.(error);
                    });
            } catch (e) {
                onFailure?.(e);
                return console.error(e.message);
            }
        };

export default forwardSlice.reducer;
